// ViewToolbar.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './ViewToolbar.css';

// Components
import CompactViewToolbar from 'components/compactviewtoolbar/CompactViewToolbar';
import ViewSelectorBar from 'components/viewtoolbar/ViewSelectorBar';
import ViewToolbarFacetMenu from 'components/viewtoolbar/ViewToolbarFacetMenu';
import ViewToolbarFieldChooser from 'components/viewtoolbar/ViewToolbarFieldChooser';
import ViewToolbarViewChips from 'components/viewtoolbar/ViewToolbarViewChips';
import DatabaseToolbarButton from 'collection/components/toolbar/button/DatabaseToolbarButton';
import DatabaseToolbarSearchButton from 'collection/components/toolbar/searchbutton/DatabaseToolbarSearchButton';
import NodeCalendarsBar from 'node/calendars/NodeCalendarsBar';
import NodeTimelinesBar from 'node/timelines/NodeTimelinesBar';
import {
    bucketViewsByGroup,
    hasConfiguredViewGroups,
} from 'utilities/ViewGroups';

/**
 * ViewToolbar
 *
 * Unified compact toolbar for database, node, and peek view surfaces.
 *
 * @returns {ReactNode} The rendered view toolbar.
 */
const ViewToolbar = React.memo(({
    activeCatchAllViewType,
    onSelectCatchAllViewType,
    theme,
    viewTitlePrefix = '',
    views = [],
    viewGroups = [],
    baseViews,
    activeViewId,
    onChangeView,
    onAddView,
    onEditView,
    onOpenEditor,
    viewsFallbackLabel = 'Saved Views',
    activeViewType = '',
    deleteActivated = false,
    onDeleteSelected,
    showFacetControls = false,
    facetFields = [],
    facetSelections = {},
    onFacetSelectionChange,
    catchAllViewTypes,
    boardFields = [],
    boardFieldKey = null,
    onBoardFieldChange,
    showFieldChooser = false,
    fieldOptions = [],
    properties = null,
    onPropertiesChange,
    calendarFields = [],
    calendarFieldKey = null,
    onCalendarFieldChange,
    showSaveView = false,
    onSaveView,
    showViewChips = false,
    searchQuery,
    onSearchQueryChange,
    showControlLabels = false,
    dividerMargin,
    className = '',
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    const resolvedViews = baseViews ?? views;

    // With view groups: chips are unsorted/global only; grouped views stay in group menus.
    const chipViews = hasConfiguredViewGroups(viewGroups)
        ? bucketViewsByGroup(resolvedViews, viewGroups).unsorted
        : resolvedViews;

    const resolvedDividerMargin = dividerMargin ?? (isMobile ? '0px' : '0px');

    const isBoardMode = String(activeCatchAllViewType || '').trim().toUpperCase() === 'BOARD';
    const isCalendarMode = String(activeCatchAllViewType || '').trim().toUpperCase() === 'CALENDAR';

    // Checklists are excluded from board cards; keep the field chooser aligned.
    const resolvedFieldOptions = isBoardMode
        ? (Array.isArray(fieldOptions) ? fieldOptions : []).filter((field) => {
            const type = String(field?.type || '').trim().toLowerCase();
            return type !== 'checklist';
        })
        : fieldOptions;

    const facetMenu = showFacetControls && (
        (Array.isArray(facetFields) && facetFields.length > 0)
        || (isBoardMode && Array.isArray(boardFields) && boardFields.length > 0)
    ) ? (
        <ViewToolbarFacetMenu
            facetFields={facetFields}
            facetSelections={facetSelections}
            showLabel={showControlLabels}
            isBoardMode={isBoardMode}
            boardFields={boardFields}
            boardFieldKey={boardFieldKey}
            onBoardFieldChange={onBoardFieldChange}
            onChange={onFacetSelectionChange}
        />
    ) : null;

    const fieldChooser = showFieldChooser ? (
        <ViewToolbarFieldChooser
            fieldOptions={resolvedFieldOptions}
            properties={properties}
            isCalendarMode={isCalendarMode}
            calendarFields={calendarFields}
            calendarFieldKey={calendarFieldKey}
            onCalendarFieldChange={onCalendarFieldChange}
            onPropertiesChange={onPropertiesChange}
        />
    ) : null;

    const hasActiveFacetSelections = (Array.isArray(facetFields) ? facetFields : []).some((field) => (
        String(facetSelections?.[field?.key] || '').trim()
    ));
    const isOrganicDraftDirty = Boolean(
        hasActiveFacetSelections
        || Array.isArray(properties)
        || isBoardMode
        || (isCalendarMode && calendarFieldKey)
    );

    const saveControl = (showSaveView && typeof onSaveView === 'function' && isOrganicDraftDirty) ? (
        <DatabaseToolbarButton
            iconKey="save"
            label={isMobile ? undefined : 'Save View'}
            title="Save the current view"
            onClick={onSaveView}
        />
    ) : null;

    const draftControls = (facetMenu || saveControl) ? (
        <div className="view-toolbar-draft-controls">
            {facetMenu}
            {saveControl}
        </div>
    ) : null;

    const viewChips = showViewChips && Array.isArray(chipViews) && chipViews.length > 0 ? (
        <ViewToolbarViewChips
            views={chipViews}
            activeViewId={activeViewId}
            onChangeView={onChangeView}
        />
    ) : null;

    const searchControl = typeof onSearchQueryChange === 'function' ? (
        <DatabaseToolbarSearchButton
            value={searchQuery}
            onChange={onSearchQueryChange}
        />
    ) : null;

    const utilityControls = (
        fieldChooser
        || searchControl
        || activeViewType === 'CALENDAR'
        || activeViewType === 'TIMELINE'
    ) ? (
        <div className="view-toolbar-utility-controls">
            {activeViewType === 'CALENDAR' ? <NodeCalendarsBar /> : null}
            {activeViewType === 'TIMELINE' ? <NodeTimelinesBar /> : null}
            {fieldChooser}
            {searchControl}
        </div>
    ) : null;

    // ----- RENDER ----- //

    return (
        <div className={`view-toolbar${className ? ` ${className}` : ''}`}>

            {/* LEFT */}
            <div
                className="view-toolbar-left"
                style={{
                    paddingLeft: isMobile ? 20 : 0,
                }}
            >
                <CompactViewToolbar
                    theme={theme}
                    selectedType={activeCatchAllViewType}
                    types={catchAllViewTypes}
                    dividerMargin={resolvedDividerMargin}
                    forceCompactTypeSelector={true}
                    viewTitlePrefix={viewTitlePrefix}
                    draftControls={draftControls}
                    viewsControl={(
                        <ViewSelectorBar
                            views={resolvedViews}
                            viewGroups={viewGroups}
                            activeViewId={activeViewId}
                            onChangeView={onChangeView}
                            onAddView={onAddView}
                            onEditView={onEditView}
                            onOpenEditor={onOpenEditor}
                            showLabel={showControlLabels}
                            fallbackLabel={viewsFallbackLabel}
                        />
                    )}
                    viewChips={viewChips}
                    utilityControls={utilityControls}
                    onSelectType={onSelectCatchAllViewType}
                />
            </div>

            {/* ACTIONS */}
            <div
                className="view-toolbar-actions"
                style={{
                    paddingRight: isMobile ? 11 : 0,
                }}
            >
                {deleteActivated ? (
                    <DatabaseToolbarButton
                        iconKey="delete"
                        label="Delete Selected"
                        onClick={onDeleteSelected}
                    />
                ) : null}
            </div>
        </div>
    );
});

// Dev Tools
ViewToolbar.displayName = 'ViewToolbar';

export default ViewToolbar;
